import { createContext, useContext, useState, useEffect } from "react";

// Default UI configuration
const defaultUIConfig = {
  fontFamily: "system-ui, -apple-system, sans-serif",
  fontSize: "medium", // small, medium, large
  primaryColor: "#1a1a2e",
  accentColor: "#CCFF00",
  borderRadius: "8px",
  compactMode: false,
};

// Font family options
export const fontFamilies = [
  { value: "'IBM Plex Sans', sans-serif", label: "IBM Plex Sans (padrão)" },
  { value: "system-ui, -apple-system, sans-serif", label: "Sistema" },
  { value: "'Inter', sans-serif", label: "Inter" },
  { value: "'Roboto', sans-serif", label: "Roboto" },
  { value: "'Open Sans', sans-serif", label: "Open Sans" },
  { value: "'Poppins', sans-serif", label: "Poppins" },
  { value: "'Montserrat', sans-serif", label: "Montserrat" },
];

// Font size options
export const fontSizes = [
  { value: "small", label: "Pequeno", scale: 0.875 },
  { value: "medium", label: "Médio", scale: 1 },
  { value: "large", label: "Grande", scale: 1.125 },
];

// Color presets
export const colorPresets = [
  { primary: "#1a1a2e", accent: "#CCFF00", label: "Evolutt" },
  { primary: "#0f172a", accent: "#38bdf8", label: "Azul" },
  { primary: "#14532d", accent: "#84cc16", label: "Verde" },
  { primary: "#7c2d12", accent: "#fb923c", label: "Laranja" },
  { primary: "#581c87", accent: "#c084fc", label: "Roxo" },
  { primary: "#1e3a5f", accent: "#60a5fa", label: "Oceano" },
];

const UIConfigContext = createContext(null);

export const UIConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(() => {
    // Load from localStorage
    const saved = localStorage.getItem("evollutt_ui_config");
    if (saved) {
      try {
        return { ...defaultUIConfig, ...JSON.parse(saved) };
      } catch {
        return defaultUIConfig;
      }
    }
    return defaultUIConfig;
  });

  // Save to localStorage on change
  useEffect(() => {
    localStorage.setItem("evollutt_ui_config", JSON.stringify(config));
    
    // Apply CSS variables to document
    document.documentElement.style.setProperty("--ui-font-family", config.fontFamily);
    document.documentElement.style.setProperty("--ui-primary-color", config.primaryColor);
    document.documentElement.style.setProperty("--ui-accent-color", config.accentColor);
    document.documentElement.style.setProperty("--ui-border-radius", config.borderRadius);
    
    // Apply font size
    const sizeOption = fontSizes.find(s => s.value === config.fontSize);
    if (sizeOption) {
      document.documentElement.style.setProperty("--ui-font-scale", sizeOption.scale.toString());
    }
    
    // Apply compact mode
    if (config.compactMode) {
      document.documentElement.classList.add("ui-compact");
    } else {
      document.documentElement.classList.remove("ui-compact");
    }
  }, [config]);

  const updateConfig = (updates) => {
    setConfig(prev => ({ ...prev, ...updates }));
  };

  const resetConfig = () => {
    setConfig(defaultUIConfig);
    localStorage.removeItem("evollutt_ui_config");
  };

  return (
    <UIConfigContext.Provider value={{ config, updateConfig, resetConfig }}>
      {children}
    </UIConfigContext.Provider>
  );
};

export const useUIConfig = () => {
  const context = useContext(UIConfigContext);
  if (!context) {
    throw new Error("useUIConfig must be used within UIConfigProvider");
  }
  return context;
};

export default UIConfigContext;
